import { useCallback, useMemo } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { config } from "../../../shared/config/environment";
import { fetchWithRefresh } from "../../../shared/api/fetchWithRefresh";
import { createDocuparserClient } from "../services/docuparserApi";
import type { DocuparserClient } from "../services/docuparserApi";

/**
 * Host-direct auth for  DocuParser , It has no individual
 * connect/disconnect UI of its own. Requests are authorised with the add-in's
 * own AIVA Azure AD session (`apiToken ?? graphToken`), with the standard
 * 401-retry-once via fetchWithRefresh.
 *
 * This is what `useDocuparserClient` returns — AIVA-7127's DocuParser.Endpoints
 * is the real backend.
 */
export function useDocuparserHttp(): DocuparserClient {
  const { apiToken, graphToken, refreshTokens } = useAuthState();
  const token = apiToken ?? graphToken;

  const authorizedFetch = useCallback(
    (url: string, init: RequestInit = {}) => {
      const headers: Record<string, string> = {
        ...(init.headers as Record<string, string> | undefined),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      return fetchWithRefresh(url, { ...init, headers }, () => refreshTokens());
    },
    [token, refreshTokens],
  );

  const apiBaseUrl = config.docuparserApiBaseUrl;
  return useMemo(
    () => createDocuparserClient(authorizedFetch, apiBaseUrl),
    [authorizedFetch, apiBaseUrl],
  );
}
